import React, { useState, useCallback, useEffect } from "react";
import {
    postCleverProvisionBatch,
    CleverProvisionStudentRow,
    CleverProvisionFailure,
} from "../services/cleverProvisionService";
import { fetchSubscriptionLimits, isUnlimitedLimits, SubscriptionLimits } from "../services/subscriptionLimitsService";
import "../styles/add-team-member-modal.css";

type Row = CleverProvisionStudentRow;

function emptyRow(): Row {
    return { email: "", firstName: "", lastName: "" };
}

type Props = {
    open: boolean;
    onClose: () => void;
    /** Called after successful API call (including partial success with some failures). */
    onProvisioned?: () => void;
    /** When adding a row or saving would exceed the subscription athlete limit — parent should show SubscriptionLimitModal. */
    onAthleteLimitReached?: () => void;
};

export default function ProvisionStudentModal({
    open,
    onClose,
    onProvisioned,
    onAthleteLimitReached,
}: Props) {
    const [rows, setRows] = useState<Row[]>([emptyRow()]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [failures, setFailures] = useState<CleverProvisionFailure[] | null>(null);
    const [limits, setLimits] = useState<SubscriptionLimits | null>(null);
    const [limitsLoading, setLimitsLoading] = useState(false);

    const reset = useCallback(() => {
        setRows([emptyRow()]);
        setError(null);
        setFailures(null);
    }, []);

    useEffect(() => {
        if (!open) {
            reset();
            setLimits(null);
            setLimitsLoading(false);
            return;
        }

        let cancelled = false;
        setLimits(null);
        setLimitsLoading(true);
        fetchSubscriptionLimits()
            .then((data) => {
                if (!cancelled) setLimits(data);
            })
            .catch(() => {
                if (!cancelled) {
                    setError("Could not load subscription limits. Please try again.");
                }
            })
            .finally(() => {
                if (!cancelled) setLimitsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, reset]);

    if (!open) return null;

    const updateRow = (index: number, field: keyof Row, value: string) => {
        setRows((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const addRow = () => {
        if (saving || limitsLoading || !limits) return;
        if (isUnlimitedLimits(limits)) {
            setRows((prev) => [...prev, emptyRow()]);
            return;
        }
        const cap = limits.remainingSlots;
        if (rows.length >= cap) {
            onAthleteLimitReached?.();
            return;
        }
        setRows((prev) => [...prev, emptyRow()]);
    };

    const removeRow = (index: number) => {
        setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setFailures(null);

        if (limitsLoading) {
            setError("Subscription limits are still loading. Please wait.");
            return;
        }
        if (!limits) {
            return;
        }

        const trimmed: Row[] = rows.map((r) => ({
            email: r.email.trim(),
            firstName: r.firstName.trim(),
            lastName: r.lastName.trim(),
        }));

        const incomplete = trimmed.some((r) => !r.email || !r.firstName || !r.lastName);
        if (incomplete) {
            setError("Please fill in email, first name, and last name for each row.");
            return;
        }

        if (!isUnlimitedLimits(limits) && trimmed.length > limits.remainingSlots) {
            onAthleteLimitReached?.();
            return;
        }

        setSaving(true);
        try {
            const result = await postCleverProvisionBatch(trimmed);
            setFailures(result.failures.length > 0 ? result.failures : null);
            if (result.created.length > 0) {
                onProvisioned?.();
            }
            if (result.failures.length === 0) {
                reset();
                onClose();
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Provisioning failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (!saving) {
            reset();
            onClose();
        }
    };

    return (
        <div className="team-modal" role="dialog" aria-modal="true" aria-labelledby="provision-student-title">
            <div className="team-modal-card team-modal-card--provision-student">
                <button className="team-modal-close" type="button" aria-label="Close" onClick={handleClose}>
                    ×
                </button>
                <div className="team-modal-content" style={{ padding: "24px", paddingTop: "48px" }}>
                    <h2 id="provision-student-title" className="form-title" style={{ marginTop: 0 }}>
                        Provision student
                    </h2>
                    <p style={{ color: "#4b5563", fontSize: "0.95rem", marginBottom: "1rem" }}>
                        Enter each student&apos;s school email and name. They must use the same email when signing in
                        with Clever. They will receive login instructions by email.
                    </p>

                    {limitsLoading ? (
                        <div style={{ padding: "24px", textAlign: "center" }}>
                            <div className="spinner" />
                            <p style={{ marginTop: "12px", color: "#4b5563" }}>Checking subscription limits…</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                {rows.map((row, index) => (
                                    <div key={index} className="provision-student-grid">
                                        <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                            <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>Email</span>
                                            <input
                                                type="email"
                                                className="input"
                                                value={row.email}
                                                onChange={(ev) => updateRow(index, "email", ev.target.value)}
                                                autoComplete="off"
                                                disabled={saving}
                                            />
                                        </label>
                                        <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                            <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>First name</span>
                                            <input
                                                className="input"
                                                value={row.firstName}
                                                onChange={(ev) => updateRow(index, "firstName", ev.target.value)}
                                                autoComplete="off"
                                                disabled={saving}
                                            />
                                        </label>
                                        <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                            <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>Last name</span>
                                            <input
                                                className="input"
                                                value={row.lastName}
                                                onChange={(ev) => updateRow(index, "lastName", ev.target.value)}
                                                autoComplete="off"
                                                disabled={saving}
                                            />
                                        </label>
                                        <div className="provision-student-row-actions">
                                            <button
                                                type="button"
                                                className="provision-student-remove"
                                                onClick={() => removeRow(index)}
                                                disabled={saving || rows.length <= 1}
                                                aria-label="Remove row"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                type="button"
                                className="btn"
                                style={{ marginTop: "12px" }}
                                onClick={addRow}
                                disabled={saving || limitsLoading || !limits}
                            >
                                + Add another student
                            </button>

                            {error && (
                                <div className="ad-alert ad-alert-error" style={{ marginTop: "12px" }}>
                                    {error}
                                </div>
                            )}

                            {failures && failures.length > 0 && (
                                <div
                                    role="alert"
                                    style={{
                                        marginTop: "12px",
                                        padding: "12px",
                                        background: "#fff7ed",
                                        border: "1px solid #fdba74",
                                        borderRadius: "8px",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    <strong>Some students could not be added:</strong>
                                    <ul style={{ margin: "8px 0 0", paddingLeft: "1.25rem" }}>
                                        {failures.map((f, i) => (
                                            <li key={i}>
                                                {f.email}: {f.message}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div
                                className="provision-student-footer"
                                style={{ display: "flex", gap: "12px", marginTop: "20px", justifyContent: "flex-end" }}
                            >
                                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-red provision-student-save" disabled={saving}>
                                    {saving ? "Saving…" : "Save"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
